import { useCallback, useEffect, useMemo, useState } from 'react'
import { CalendarSearch, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { fetchAppointments } from '@/lib/api/appointments'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import {
  appointmentGuestLabel,
  appointmentLookupSummary,
  appointmentServiceLabel,
  dayOccupancyLabel,
  isGuestSearchQuery,
  lookupRangeForDate,
  lookupRangeForGuestSearch,
  occupyingAppointments,
} from '@/lib/calendar/appointmentLookup'
import { formatDayHeading } from '@/lib/calendar/calendarDates'
import { cn } from '@/lib/utils'

/**
 * @param {object} props
 * @param {string} props.selectedDate
 * @param {(date: string) => void} props.onSelectDate
 * @param {(appointment: import('@/types/api').Appointment) => void} props.onSelectAppointment
 * @param {() => void} [props.onBookDate]
 */
export function AppointmentLookupPanel({
  selectedDate,
  onSelectDate,
  onSelectAppointment,
  onBookDate,
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [appointments, setAppointments] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const guestSearch = isGuestSearchQuery(searchQuery)

  const loadLookup = useCallback(async () => {
    setLoading(true)
    setError('')

    const range = guestSearch
      ? lookupRangeForGuestSearch()
      : lookupRangeForDate(selectedDate)

    try {
      const items = await fetchAppointments({
        ...range,
        q: guestSearch ? searchQuery.trim() : undefined,
      })
      setAppointments(items)
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load bookings.'))
      setAppointments([])
    } finally {
      setLoading(false)
    }
  }, [guestSearch, searchQuery, selectedDate])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadLookup()
    }, guestSearch ? 250 : 0)

    return () => window.clearTimeout(timer)
  }, [guestSearch, loadLookup])

  const visibleAppointments = useMemo(() => {
    const active = occupyingAppointments(appointments)

    if (guestSearch) {
      return active
    }

    return active.filter((appointment) => appointment.starts_at.slice(0, 10) === selectedDate)
  }, [appointments, guestSearch, selectedDate])

  const summary = guestSearch
    ? visibleAppointments.length === 0
      ? `No upcoming bookings for “${searchQuery.trim()}”`
      : `${visibleAppointments.length} upcoming booking${visibleAppointments.length === 1 ? '' : 's'} for “${searchQuery.trim()}”`
    : dayOccupancyLabel(visibleAppointments.length, selectedDate)

  return (
    <section className="card-surface flex min-h-0 flex-col overflow-hidden">
      <div className="border-b border-border/60 px-4 py-3">
        <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <CalendarSearch className="h-4 w-4 text-primary" />
          Find booking
        </p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Jump to any date or search guest name / phone.
        </p>
      </div>

      <div className="space-y-3 border-b border-border/60 px-4 py-3">
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            View date
          </span>
          <input
            type="date"
            value={selectedDate}
            onChange={(event) => onSelectDate(event.target.value)}
            className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Search guest
          </span>
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Name or phone…"
            className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
          />
        </label>

        <p className="text-xs font-medium text-foreground">{summary}</p>
      </div>

      {error ? (
        <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700">
          {error}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : visibleAppointments.length === 0 ? (
          <div className="space-y-3 py-6 text-center">
            <p className="text-sm text-muted-foreground">
              {guestSearch
                ? 'Try another name or phone number.'
                : 'This day has no active bookings yet.'}
            </p>
            {!guestSearch && onBookDate ? (
              <Button type="button" size="sm" variant="outline" onClick={onBookDate}>
                Book on {formatDayHeading(selectedDate)}
              </Button>
            ) : null}
          </div>
        ) : (
          <ul className="space-y-2">
            {visibleAppointments.map((appointment) => (
              <li key={appointment.id}>
                <button
                  type="button"
                  onClick={() => onSelectAppointment(appointment)}
                  className="w-full rounded-xl border border-border/60 bg-card px-3 py-3 text-left transition-colors hover:bg-muted/30"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-foreground">
                        {appointmentGuestLabel(appointment)}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {appointmentServiceLabel(appointment)}
                      </p>
                      <p className="mt-1 text-[11px] font-medium text-primary">
                        {appointmentLookupSummary(appointment)}
                      </p>
                    </div>
                    <span
                      className={cn(
                        'shrink-0 rounded-lg px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                        appointment.status === 'scheduled'
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {appointment.status?.replace('_', ' ')}
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
